import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';
import { countryFlag, statusColor } from '../../lib/country-utils';

/* ── Types ── */
interface TourismCountry {
  country: string;
  country_code: string;
  city_count: number;
  statuses: string;
}

interface CountryLaw {
  country: string;
  country_code?: string;
  legal_status?: string;
  possession_limit?: string;
  penalties?: string;
  medical_program?: string;
  recreational?: string;
  notes?: string;
}

interface TourismCity {
  id: number;
  country: string;
  country_code?: string;
  city: string;
  tourist_legal_status?: string;
  can_tourists_buy?: string;
  min_age?: number;
  where_to_buy?: string;
  possession_limit?: string;
  consumption_rules?: string;
  avg_price_gram?: string;
  popular_strains?: string[];
  top_spots?: string[];
  cultural_tips?: string;
  warnings?: string;
  best_season?: string;
  vibe?: string;
  description?: string;
}

interface Dispensary {
  id: number;
  name: string;
  city?: string;
  country?: string;
  type?: string;
  rating?: number;
  website?: string;
  address?: string;
}

interface PriceEntry {
  country: string;
  city?: string;
  avg_price_gram_usd?: number;
  currency?: string;
  quality_tier?: string;
}

/* ── Helpers ── */
function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

function SectionCard({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
      >
        <h3 className="text-white font-semibold text-sm">{title}</h3>
        <span className="text-white/30 text-xs">{isOpen ? 'Collapse' : 'Expand'}</span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-white/5 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-white/5 last:border-0">
      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1 font-medium">{label}</p>
      <p className="text-white/75 text-xs leading-relaxed">{value}</p>
    </div>
  );
}

export default function TripPlannerTab({ userCountry, isGlobal }: CountryProps) {
  const [countries, setCountries] = useState<TourismCountry[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Destination data
  const [laws, setLaws] = useState<CountryLaw | null>(null);
  const [cities, setCities] = useState<TourismCity[]>([]);
  const [dispensaries, setDispensaries] = useState<Dispensary[]>([]);
  const [prices, setPrices] = useState<PriceEntry[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Section open state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    legal: true,
    cities: true,
    dispensaries: false,
    events: false,
    prices: false,
    tips: false,
  });

  const [expandedCity, setExpandedCity] = useState<number | null>(null);

  const toggleSection = (key: string) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Fetch country list
  useEffect(() => {
    async function load() {
      try {
        const [tourismRes, lawsRes] = await Promise.all([
          fetch(getApiUrl('/api/cannabis-tourism/countries')),
          fetch(getApiUrl('/api/country-laws')),
        ]);
        const tourismData: TourismCountry[] = tourismRes.ok ? await tourismRes.json() : [];
        const lawsData: CountryLaw[] = lawsRes.ok
          ? await lawsRes.json().then((d: CountryLaw[] | { data: CountryLaw[] }) =>
              Array.isArray(d) ? d : (d as { data: CountryLaw[] }).data ?? []
            )
          : [];

        // Build full list: all countries from laws, enriched with tourism data
        const tourismMap = new Map(tourismData.map(t => [t.country, t]));
        const allCountries: TourismCountry[] = lawsData.map(l => {
          const t = tourismMap.get(l.country);
          return {
            country: l.country,
            country_code: l.country_code || t?.country_code || '',
            city_count: t?.city_count ?? 0,
            statuses: t?.statuses || l.legal_status || '',
          };
        });

        // Add any tourism countries not in laws
        for (const t of tourismData) {
          if (!allCountries.find(c => c.country === t.country)) {
            allCountries.push(t);
          }
        }

        allCountries.sort((a, b) => a.country.localeCompare(b.country));
        setCountries(allCountries);
      } catch {
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    }
    load();
  }, []);

  // Fetch destination data when a country is selected
  useEffect(() => {
    if (!selectedCountry) return;
    setLoadingDetail(true);
    setLaws(null);
    setCities([]);
    setDispensaries([]);
    setPrices([]);
    setExpandedCity(null);

    async function fetchAll() {
      try {
        const [lawsRes, tourismRes, dispRes, priceRes] = await Promise.all([
          fetch(getApiUrl(`/api/country-laws?search=${encodeURIComponent(selectedCountry!)}`)),
          fetch(getApiUrl(`/api/cannabis-tourism?country=${encodeURIComponent(selectedCountry!)}`)),
          fetch(getApiUrl(`/api/dispensaries-world?country=${encodeURIComponent(selectedCountry!)}&per_page=50`)),
          fetch(getApiUrl('/api/price-index')),
        ]);

        // Laws
        if (lawsRes.ok) {
          const lawsData = await lawsRes.json();
          const arr: CountryLaw[] = Array.isArray(lawsData) ? lawsData : lawsData.data ?? [];
          const match = arr.find(
            l => typeof l.country === 'string' && typeof selectedCountry === 'string' && l.country.toLowerCase() === selectedCountry.toLowerCase()
          );
          setLaws(match || arr[0] || null);
        }

        // Tourism cities
        if (tourismRes.ok) {
          const tourismData = await tourismRes.json();
          const mapped = (Array.isArray(tourismData) ? tourismData : []).map(
            (r: TourismCity) => ({
              ...r,
              popular_strains: Array.isArray(r.popular_strains)
                ? r.popular_strains
                : typeof r.popular_strains === 'string'
                  ? JSON.parse(r.popular_strains || '[]')
                  : [],
              top_spots: Array.isArray(r.top_spots)
                ? r.top_spots
                : typeof r.top_spots === 'string'
                  ? JSON.parse(r.top_spots || '[]')
                  : [],
            })
          );
          setCities(mapped);
        }

        // Dispensaries
        if (dispRes.ok) {
          const dispData = await dispRes.json();
          setDispensaries(dispData.items || []);
        }

        // Prices for this country
        if (priceRes.ok) {
          const priceData: PriceEntry[] = await priceRes.json();
          setPrices(
            priceData.filter(
              p => typeof p.country === 'string' && typeof selectedCountry === 'string' && p.country.toLowerCase() === selectedCountry.toLowerCase()
            )
          );
        }
      } catch {
        // Silently handle — partial data is fine
      } finally {
        setLoadingDetail(false);
      }
    }

    fetchAll();
  }, [selectedCountry]);

  // Filter country list
  const filteredCountries = countries.filter(c => {
    if (!search.trim()) return true;
    return typeof c.country === 'string' && c.country.toLowerCase().includes(search.toLowerCase());
  });

  // If user country is set, push it to the top
  if (userCountry && !isGlobal) {
    const idx = filteredCountries.findIndex(c => c.country === userCountry.name);
    if (idx > 0) {
      const [item] = filteredCountries.splice(idx, 1);
      filteredCountries.unshift(item);
    }
  }

  // Gather cultural tips and warnings from tourism data
  const culturalTips = cities
    .filter(c => c.cultural_tips)
    .map(c => ({ city: c.city, tips: c.cultural_tips! }));
  const warnings = cities
    .filter(c => c.warnings)
    .map(c => ({ city: c.city, warning: c.warnings! }));

  /* ── STEP 1: Pick Destination ── */
  if (!selectedCountry) {
    if (loadingCountries) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(9)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-white font-semibold text-lg">Plan Your Cannabis Trip</h2>
          <p className="text-white/40 text-sm mt-1">
            Select a destination to get a full overview: laws, cities, dispensaries, prices, and travel tips.
          </p>
        </div>

        <input
          type="text"
          placeholder="Search countries..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ade80]/40 transition-colors"
        />

        {filteredCountries.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <p>No countries match your search.</p>
          </div>
        ) : (
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {filteredCountries.map(c => (
              <motion.button
                key={c.country}
                variants={staggerItem}
                onClick={() => {
                  setSelectedCountry(c.country);
                  setSearch('');
                }}
                className="group text-left rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-[#4ade80]/30 hover:bg-white/[0.04] transition-all duration-200"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">
                    {countryFlag(c.country_code)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{c.country}</p>
                    {c.city_count > 0 && (
                      <p className="text-white/40 text-xs mt-0.5">
                        {c.city_count} {c.city_count === 1 ? 'city' : 'cities'} with tourism data
                      </p>
                    )}
                    {c.statuses && (
                      <span
                        className={`inline-block mt-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColor(c.statuses)}`}
                      >
                        {c.statuses}
                      </span>
                    )}
                    {userCountry && !isGlobal && c.country === userCountry.name && (
                      <span className="ml-2 px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80]/30">
                        Your Country
                      </span>
                    )}
                  </div>
                  <span className="text-white/20 group-hover:text-white/50 transition-colors mt-1">
                    &#8594;
                  </span>
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}
      </div>
    );
  }

  /* ── STEP 2: Destination Overview ── */
  const countryInfo = countries.find(c => c.country === selectedCountry);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSelectedCountry(null)}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors"
        >
          &#8592;
        </button>
        {countryInfo && (
          <span className="text-2xl">{countryFlag(countryInfo.country_code)}</span>
        )}
        <div>
          <h2 className="text-white font-semibold text-lg">{selectedCountry}</h2>
          <p className="text-white/40 text-xs">Trip Overview</p>
        </div>
      </div>

      {loadingDetail ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-3"
        >
          {/* A) Legal Status */}
          <motion.div variants={staggerItem}>
            <SectionCard
              title="Legal Status"
              isOpen={openSections.legal}
              onToggle={() => toggleSection('legal')}
            >
              {laws ? (
                <div className="space-y-0">
                  {laws.legal_status && (
                    <div className="pb-3">
                      <span
                        className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${statusColor(laws.legal_status)}`}
                      >
                        {laws.legal_status}
                      </span>
                    </div>
                  )}
                  <InfoRow label="Possession Limit" value={laws.possession_limit} />
                  <InfoRow label="Penalties" value={laws.penalties} />
                  <InfoRow label="Medical Program" value={laws.medical_program} />
                  <InfoRow label="Recreational" value={laws.recreational} />
                  <InfoRow label="Notes" value={laws.notes} />
                </div>
              ) : (
                <p className="text-white/40 text-xs">No legal data available for this country.</p>
              )}
            </SectionCard>
          </motion.div>

          {/* B) Cities to Visit */}
          {cities.length > 0 && (
            <motion.div variants={staggerItem}>
              <SectionCard
                title={`Cities to Visit (${cities.length})`}
                isOpen={openSections.cities}
                onToggle={() => toggleSection('cities')}
              >
                <div className="space-y-3">
                  {cities.map(city => {
                    const isExpanded = expandedCity === city.id;
                    return (
                      <div
                        key={city.id}
                        className={`rounded-xl border p-3.5 transition-colors ${
                          isExpanded
                            ? 'border-[#4ade80]/30 bg-white/[0.03]'
                            : 'border-white/10 bg-white/[0.01] hover:border-white/20'
                        }`}
                      >
                        <button
                          onClick={() => setExpandedCity(isExpanded ? null : city.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className="text-white font-semibold text-sm">{city.city}</h4>
                              {city.vibe && (
                                <p className="text-white/40 text-xs mt-0.5 italic">{city.vibe}</p>
                              )}
                            </div>
                            <div className="shrink-0 text-right space-y-1">
                              {city.can_tourists_buy && (
                                <p
                                  className={`text-xs font-semibold ${
                                    city.can_tourists_buy === 'Yes'
                                      ? 'text-green-400'
                                      : city.can_tourists_buy === 'No'
                                        ? 'text-red-400'
                                        : 'text-amber-400'
                                  }`}
                                >
                                  {city.can_tourists_buy === 'Yes'
                                    ? 'Tourists can buy'
                                    : city.can_tourists_buy === 'No'
                                      ? 'Cannot buy'
                                      : city.can_tourists_buy}
                                </p>
                              )}
                              {city.avg_price_gram && (
                                <p className="text-white/30 text-[11px]">{city.avg_price_gram}/g</p>
                              )}
                            </div>
                          </div>
                          <p className="text-white/20 text-[11px] mt-1.5">
                            {isExpanded ? 'Click to collapse' : 'Click for details'}
                          </p>
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-3 pt-3 border-t border-white/10 space-y-0">
                                {city.description && (
                                  <p className="text-white/60 text-xs leading-relaxed pb-2">
                                    {city.description}
                                  </p>
                                )}
                                <InfoRow label="Where to buy" value={city.where_to_buy} />
                                <InfoRow label="Possession limit" value={city.possession_limit} />
                                <InfoRow label="Consumption rules" value={city.consumption_rules} />
                                <InfoRow label="Best season" value={city.best_season} />
                                <InfoRow label="Min age" value={city.min_age?.toString()} />

                                {city.popular_strains && city.popular_strains.length > 0 && (
                                  <div className="py-2 border-b border-white/5">
                                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 font-medium">
                                      Popular strains
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {city.popular_strains.map((s, i) => (
                                        <span
                                          key={i}
                                          className="px-2 py-0.5 text-[11px] rounded-full bg-green-500/10 text-green-300/70 border border-green-500/20"
                                        >
                                          {s}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {city.top_spots && city.top_spots.length > 0 && (
                                  <div className="py-2">
                                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 font-medium">
                                      Top spots
                                    </p>
                                    <ul className="space-y-1">
                                      {city.top_spots.map((s, i) => (
                                        <li
                                          key={i}
                                          className="text-white/60 text-xs pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-white/20"
                                        >
                                          {s}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {/* C) Dispensaries */}
          {dispensaries.length > 0 && (
            <motion.div variants={staggerItem}>
              <SectionCard
                title={`Dispensaries (${dispensaries.length})`}
                isOpen={openSections.dispensaries}
                onToggle={() => toggleSection('dispensaries')}
              >
                <div className="space-y-2">
                  {dispensaries.map(d => (
                    <div
                      key={d.id}
                      className="flex items-start justify-between gap-3 py-2.5 border-b border-white/5 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="text-white font-medium text-sm">{d.name}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {d.city && (
                            <span className="text-white/40 text-xs">{d.city}</span>
                          )}
                          {d.type && (
                            <span className="text-white/30 text-xs">{d.type}</span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right space-y-1">
                        {d.rating != null && d.rating > 0 && (
                          <span className="text-[#d4a574] text-xs font-semibold">
                            {d.rating}/5
                          </span>
                        )}
                        {d.website && (
                          <a
                            href={d.website.startsWith('http') ? d.website : `https://${d.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[#4ade80]/70 text-[11px] hover:text-[#4ade80] transition-colors"
                          >
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {/* D) Upcoming Events */}
          <motion.div variants={staggerItem}>
            <SectionCard
              title="Upcoming Events"
              isOpen={openSections.events}
              onToggle={() => toggleSection('events')}
            >
              <p className="text-white/50 text-xs leading-relaxed">
                Check the <span className="text-[#4ade80]">Guides</span> tab for cannabis festivals and events in{' '}
                <span className="text-white/70 font-medium">{selectedCountry}</span>.
                Festival dates and locations are updated regularly in our database.
              </p>
            </SectionCard>
          </motion.div>

          {/* E) Price Guide */}
          {(prices.length > 0 || cities.some(c => c.avg_price_gram)) && (
            <motion.div variants={staggerItem}>
              <SectionCard
                title="Price Guide"
                isOpen={openSections.prices}
                onToggle={() => toggleSection('prices')}
              >
                {prices.length > 0 ? (
                  <div className="space-y-2">
                    {prices.map((p, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                      >
                        <div>
                          <p className="text-white/70 text-sm">{p.city || p.country}</p>
                          {p.quality_tier && (
                            <p className="text-white/30 text-[11px]">{p.quality_tier}</p>
                          )}
                        </div>
                        {p.avg_price_gram_usd != null && (
                          <span className="text-[#4ade80] font-semibold text-sm">
                            ${p.avg_price_gram_usd}/g
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2 font-medium">
                      Average prices from tourism data
                    </p>
                    {cities
                      .filter(c => c.avg_price_gram)
                      .map(c => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0"
                        >
                          <span className="text-white/60 text-xs">{c.city}</span>
                          <span className="text-[#4ade80]/70 text-xs font-medium">
                            {c.avg_price_gram}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </SectionCard>
            </motion.div>
          )}

          {/* F) Travel Tips */}
          {(culturalTips.length > 0 || warnings.length > 0) && (
            <motion.div variants={staggerItem}>
              <SectionCard
                title="Travel Tips"
                isOpen={openSections.tips}
                onToggle={() => toggleSection('tips')}
              >
                <div className="space-y-4">
                  {culturalTips.length > 0 && (
                    <div>
                      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2 font-medium">
                        Cultural Tips
                      </p>
                      {culturalTips.map((t, i) => (
                        <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                          <p className="text-white/50 text-[11px] font-medium mb-0.5">{t.city}</p>
                          <p className="text-white/60 text-xs leading-relaxed">{t.tips}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {warnings.length > 0 && (
                    <div>
                      <p className="text-red-400/60 text-[10px] uppercase tracking-wider mb-2 font-medium">
                        Warnings
                      </p>
                      {warnings.map((w, i) => (
                        <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                          <p className="text-white/50 text-[11px] font-medium mb-0.5">{w.city}</p>
                          <p className="text-red-300/60 text-xs leading-relaxed">{w.warning}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {/* Fallback if no data at all besides laws */}
          {cities.length === 0 && dispensaries.length === 0 && prices.length === 0 && (
            <motion.div variants={staggerItem}>
              <div className="text-center py-8 text-white/30 text-sm">
                Limited data available for {selectedCountry}. Legal status information is shown above.
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}
